import {Component, effect, inject, signal} from '@angular/core';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {DeadlineFilterChange, DeadlineFilterComponent} from '../common/deadline-filter/deadline-filter.component';
import {BadgeUserData, PersonPickerComponent} from '../common/person-picker/person-picker.component';
import {User} from '../../models/user';
import {StatusGroup} from '../common/status-parser.pipe';
import {ItemCounterComponent} from '../common/item-counter/item-counter.component';
import {ButtonWLoaderComponent} from '../common/button-w-loader/button-w-loader.component';
import {MatInput} from '@angular/material/input';
import {LaService} from '../services/la.service';
import {LA, LACountsResponse, LAFilterParams} from '../../models/la';
import {LATableComponent} from './la-table/la-table.component';
import {MatDialog} from '@angular/material/dialog';
import {CreateLaComponent} from './create-la/create-la.component';
import {SnackbarService} from '../services/snackbar.service';
import {ActivatedRoute, Router} from '@angular/router';
import {PageEvent} from '@angular/material/paginator';
import {Sort} from '@angular/material/sort';
import {Location} from '@angular/common';
import {MatTooltip} from '@angular/material/tooltip';
import dayjs from 'dayjs';

@Component({
  selector: 'app-legislative-action',
  imports: [
    MatFormField,
    PersonPickerComponent,
    DeadlineFilterComponent,
    ItemCounterComponent,
    ButtonWLoaderComponent,
    MatLabel,
    MatInput,
    LATableComponent,
    MatTooltip,

  ],
  templateUrl: './legislative-action.component.html',
  standalone: true,
  styleUrl: './legislative-action.component.scss'
})
export class LegislativeActionComponent {
  private readonly sortColumnMap: Record<string, NonNullable<LAFilterParams['sortBy']>> = {
    number: 'number',
    date: 'date',
    _type: 'type',
    _subject: 'subject',
    _theme: 'theme',
  };

  currentSort: Pick<LAFilterParams, 'sortBy' | 'sortOrder'> = {};

  filterToday: boolean = false;
  filterUntil: boolean = false;

  filterDate?: Date;
  filterValue: string = '';
  laId: string | null = null;
  usersToFilter: BadgeUserData[] = [];
  selectedUserIds: string[] = [];
  usersToShowBadges?: User[];
  laList: LA[] = [];

  totalItems = signal(0);
  pageSize = signal(10);

  statusMap = new Map<string, StatusGroup>([
    ['total', StatusGroup.total.setShowSeparator(true)],
    ['open', StatusGroup.open],
    ['inProgress', StatusGroup.inProgress],
    ['approved', StatusGroup.approved],
    ['sanctioned', StatusGroup.sanctioned],
    ['rejected', StatusGroup.rejected],
    ['archived', StatusGroup.archived],
  ]);

  activatedRoute = inject(ActivatedRoute);
  location = inject(Location);

  constructor(
    private laService: LaService,
    private snackService: SnackbarService,
    private router: Router

  ) {
    this.activatedRoute.paramMap.subscribe((param) => {
      this.location.go('/acoes-legislativas')
      this.laId = param.get('laId') ?? null;
      this.update();
    });
    effect(() => {
      this.laList = this.laService.laList();
      const pagination = this.laService.laPagination();
      if (pagination) {
        this.totalItems.set(pagination.total);
      }
      const filters = this.laService.laFilters();
      if (filters) {
        this.usersToShowBadges = filters.users as User[];
      }
      const counts = this.laService.laCounts();
      this.setStatusMap(counts);
      if(this.laId && this.laList.length) {
        this.openDialog(this.laId);
        this.laId = null;
      }
    });
    if (this.laService.laTypeList().length === 0 || this.laService.laThemeList().length === 0) {
      this.laService.getAllLaInformation();
    }
  }

  async update() {
    // Called from route subscription - effect handles dialog opening
  }

  setStatusMap(counts: LACountsResponse | null) {
    const pagination = this.laService.laPagination();
    this.statusMap.get('total')?.setCount(pagination?.total ?? 0);
    this.statusMap.get('open')?.setCount(counts?.open ?? 0);
    this.statusMap.get('inProgress')?.setCount(counts?.inProgress ?? 0);
    this.statusMap.get('approved')?.setCount(counts?.approved ?? 0);
    this.statusMap.get('sanctioned')?.setCount(counts?.sanctioned ?? 0);
    this.statusMap.get('rejected')?.setCount(counts?.rejected ?? 0);
    this.statusMap.get('archived')?.setCount(counts?.archived ?? 0);
  }

  timestampChange(event: DeadlineFilterChange) {
    this.filterToday = event.today ?? false;
    this.filterUntil = event.until ?? false;
    this.applyFilters();
  }

  timestampDateChange(event?: Date) {
    this.filterDate = event;
    this.applyFilters();
  }

  personSelected(event: BadgeUserData[]) {
    this.usersToFilter = event.filter((user) => user.selected);
    this.selectedUserIds = this.usersToFilter.map(u => u.id);
    this.applyFilters();
  }

  searchValue: string = '';
  private searchTimeout?: ReturnType<typeof setTimeout>;

  newLA() {
    this.openDialog();
  }

  openDetails(taskId: string) {
    this.openDialog(taskId);
  }

  search(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue;

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.applyFilters();
    }, 300);
  }

  applyFilters() {
    const params: LAFilterParams & { offset: number } = { offset: 0, ...this.currentSort };

    if (this.searchValue) {
      params.search = this.searchValue;
    }

    if (this.usersToFilter.length > 0) {
      params.users = this.usersToFilter.map(u => u.id);
    }

    if (this.filterToday) {
      params.today = true;
    } else if (this.filterUntil && this.filterDate) {
      params.date = dayjs(this.filterDate).format('YYYY-MM-DD');
    }

    this.laService.getLAPaginated(params);
  }

  onPageChange(event: PageEvent) {
    this.pageSize.set(event.pageSize);
    this.laService.getLAPaginated({
      limit: event.pageSize,
      offset: event.pageIndex * event.pageSize,
      ...this.currentSort,
    });
  }

  onSortChange(event: Sort) {
    const apiColumn = this.sortColumnMap[event.active];
    if (!apiColumn || !event.direction) {
      this.currentSort = {};
    } else {
      this.currentSort = { sortBy: apiColumn, sortOrder: event.direction as 'asc' | 'desc' };
    }
    this.laService.getLAPaginated({
      offset: 0,
      ...this.currentSort,
    });
  }

  readonly dialog = inject(MatDialog);
  openDialog(laId?: string): void {
    const dialogRef = this.dialog.open(CreateLaComponent, {
      minWidth: '1199px',
      maxHeight: '823px',
      data: {
        laData: laId ?  this.laList.find((la) => la.id === laId) : undefined
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(async result => {
      if(result && result.new){
        const snack = this.snackService.openSnackBar('success',
          'Proposição salva com sucesso!',
          'Clique para vê-la'
        );
        snack.onAction().subscribe(() => {
          this.router.navigate(['acoes-legislativas', result.new]);
        });
      }
    });
  }
}
